import { createSelector } from "reselect";
import unionBy from "lodash/unionBy";
import { defaultEventProfileWidgets } from "orion-components/Profiles/Utils/EventProfileWidgets";
import { widgetStateSelector } from "orion-components/AppState/Selectors";
import { eventTypesSelector } from "orion-components/GlobalData/Selectors";

// Constants
const DEFAULT_WIDGETS = ["notes", "pinned_items", "files", "cameras"];
const EVENT_WIDGETS = ["cad_details", "responding_units", "gate_runner_response"];

// Selectors
const getWidgetState = (state) => widgetStateSelector(state)?.widgetOrder || [];
const getTypes = (state) => eventTypesSelector(state);
const selectIntegrations = (state) => state.session?.user?.profile?.integrations || [];

// Helper Functions
export const isWidgetEnabled = (widgetName, integrations) =>
	integrations.some((integration) => integration.feedId === widgetName);

export const getEventWidgetsToRemove = (enabledWidgets) =>
	EVENT_WIDGETS.filter((widget) => !enabledWidgets.includes(widget));

export const eventProfileWidgetConfig = createSelector(
	[getWidgetState, getTypes, selectIntegrations],
	(widgetState, types, integrations) => (event, viewLists, appId, widgetsExpandable) => {
		let enabledWidgets = [...DEFAULT_WIDGETS];

		if (viewLists) enabledWidgets.push("event_lists");

		// Don't allow proximity widget in cameras app
		if (appId !== "cameras-app") enabledWidgets.push("proximity");

		// Only allow activities widget if not a template
		if (!event.isTemplate) enabledWidgets.push("activities");

		// Only allow map widget if the widgets are expandable
		if (widgetsExpandable) enabledWidgets.push("map");

		// Superion CAD widgets based on availability of data coming from cad system
		if (event?.additionalProperties?.cadId) {
			enabledWidgets.push("cad_details");
		}

		if (event?.additionalProperties?.respondingUnits) {
			enabledWidgets.push("responding_units");
		}

		// Update widget list based on HRMS
		if (isWidgetEnabled("hrms", integrations)) {
			enabledWidgets.push("resources", "equipments");
		}

		//remove Gate Runner widget for non gate runner events
		if (event.type !== "gate_runner") {
			enabledWidgets = enabledWidgets.filter((widget) => widget !== "gate_runner_response");
		}

		if (event?.patrolEnabled) enabledWidgets.push("patrols");

		// Filter available widgets
		const availableWidgets = defaultEventProfileWidgets.filter((widget) => enabledWidgets.includes(widget.id));

		let widgetConfig = widgetState.length ? unionBy(widgetState, availableWidgets, "id") : availableWidgets;

		// Remove unnecessary widgets
		const widgetsToRemove = [];
		if (!isWidgetEnabled("hrms", integrations)) {
			widgetsToRemove.push("resources", "equipments");
		}
		widgetsToRemove.push(...getEventWidgetsToRemove(enabledWidgets));
		if (widgetsToRemove.length) {
			widgetConfig = widgetConfig.filter((widget) => !widgetsToRemove.includes(widget.id));
		}

		return widgetConfig;
	}
);
